import { useEffect, useState, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Search, Loader2, Inbox, RefreshCw, CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";
import { PageHeader } from "../components/ui/page-header";
import { EmptyState } from "../components/ui/empty-state";

const PAGE_SIZE = 10;

const STATUS_LABEL = {
    menunggu: "Menunggu",
    selesai_diperbaiki: "Selesai Diperbaiki",
    expired_reject_permanen: "Expired (Permanen)",
};

const STATUS_TONE = {
    menunggu: "bg-amber-50 text-amber-700 border-amber-200",
    selesai_diperbaiki: "bg-emerald-50 text-emerald-700 border-emerald-200",
    expired_reject_permanen: "bg-rose-50 text-rose-700 border-rose-200",
};

function deadlineTone(days, status) {
    if (status !== "menunggu") return "bg-slate-100 text-slate-500 border-slate-200";
    if (days < 3) return "bg-rose-50 text-rose-700 border-rose-200";
    if (days < 7) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
}

export default function RejectPerbaikanPage() {
    const { user } = useAuth();
    const canMark = user?.role === "admin" || user?.role === "staf_input";
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [fVendor, setFVendor] = useState("all");
    const [fStatus, setFStatus] = useState("all");
    const [page, setPage] = useState(1);

    const [confirmSelesai, setConfirmSelesai] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [checkingExpired, setCheckingExpired] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (fVendor !== "all") params.vendor_id = fVendor;
            if (fStatus !== "all") params.status = fStatus;
            const { data } = await api.get("/reject-perbaikan", { params });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { (async () => { const v = await api.get("/vendors"); setVendors(v.data); })(); }, []);
    useEffect(() => { const t = setTimeout(() => { load(); setPage(1); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, fVendor, fStatus]);

    const vendorMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.nama])), [vendors]);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const pageItems = useMemo(() => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [items, page]);

    const markSelesai = async () => {
        if (!confirmSelesai) return;
        setProcessing(true);
        try {
            await api.post(`/reject-perbaikan/${confirmSelesai.id}/mark-selesai`, {});
            toast.success("Ditandai selesai diperbaiki");
            setConfirmSelesai(null); load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setProcessing(false); }
    };

    const checkExpired = async () => {
        setCheckingExpired(true);
        try {
            const { data } = await api.post("/reject-perbaikan/check-expired", {});
            toast.success(data.message);
            load();
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setCheckingExpired(false); }
    };

    return (
        <div data-testid="reject-page">
            <PageHeader
                title="Reject Bisa Diperbaiki"
                description="Antrian barang reject yang bisa diperbaiki vendor. Batas 14 hari, lewat itu jadi reject permanen otomatis."
                actions={isAdmin && (
                    <Button onClick={checkExpired} disabled={checkingExpired} data-testid="reject-check-expired-btn" variant="outline" className="rounded-lg gap-2">
                        {checkingExpired ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Cek Expired
                    </Button>
                )}
            />

            <div className="rounded-xl bg-white p-4 mb-4" style={{ border: "1px solid var(--border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                        <Input placeholder="Cari nomor..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="reject-search-input" className="pl-9 rounded-lg h-10" />
                    </div>
                    <Select value={fVendor} onValueChange={setFVendor}>
                        <SelectTrigger className="rounded-lg h-10"><SelectValue placeholder="Semua vendor" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua vendor</SelectItem>
                            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={fStatus} onValueChange={setFStatus}>
                        <SelectTrigger data-testid="reject-filter-status" className="rounded-lg h-10"><SelectValue placeholder="Semua status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua status</SelectItem>
                            <SelectItem value="menunggu">Menunggu</SelectItem>
                            <SelectItem value="selesai_diperbaiki">Selesai Diperbaiki</SelectItem>
                            <SelectItem value="expired_reject_permanen">Expired Permanen</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--apple-blue)" }} /></div>
                ) : pageItems.length === 0 ? (
                    <div className="rounded-xl bg-white" style={{ border: "1px solid var(--border)" }}>
                        <EmptyState icon={AlertOctagon} title="Belum ada reject perbaikan" description="Belum ada barang reject untuk diperbaiki." testid="reject-empty-mobile" />
                    </div>
                ) : pageItems.map((row) => {
                    const nilai = Number(row.jumlah_pcs || 0) * Number(row.harga_per_pcs_snapshot || 0);
                    const days = row.days_remaining ?? 0;
                    return (
                        <div key={row.id} className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--border)" }} data-testid={`reject-card-${row.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-sm" style={{ color: "var(--apple-blue)" }}>{row.nomor_rp}</div>
                                    <div className="text-xs truncate" style={{ color: "var(--text-primary)" }}>{vendorMap[row.vendor_id] || "-"}</div>
                                    <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>{row.nama_produk_snapshot}</div>
                                </div>
                                <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${STATUS_TONE[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs mt-2 pt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                <div><span style={{ color: "var(--text-muted)" }}>Jumlah: </span><span className="num-tabular font-semibold">{row.jumlah_pcs} pcs</span></div>
                                <div className="text-right"><span className="font-semibold num-tabular" style={{ color: "var(--text-primary)" }}>{formatRupiah(nilai)}</span></div>
                                <div><span style={{ color: "var(--text-muted)" }}>Masuk: </span>{formatTanggal(row.tanggal_masuk)}</div>
                                <div className="text-right"><span style={{ color: "var(--text-muted)" }}>Deadline: </span>{formatTanggal(row.deadline)}</div>
                            </div>
                            {row.status === "menunggu" && canMark && (
                                <div className="mt-3 pt-2" style={{ borderTop: "1px solid var(--border-soft)" }}>
                                    <button onClick={() => setConfirmSelesai(row)} data-testid={`reject-mark-selesai-mobile-${row.id}`} className="w-full text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1 transition-colors border border-emerald-200">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Tandai Selesai Diperbaiki
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="hidden md:block rounded-xl overflow-hidden bg-white" style={{ border: "1px solid var(--border)" }}>
                <Table data-testid="reject-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Nomor RP</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Vendor</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Produk</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Jumlah</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500 text-right">Nilai</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Masuk</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Deadline</TableHead>
                            <TableHead className="text-xs font-semibold uppercase text-slate-500">Status</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase text-slate-500 w-32">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={9} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : pageItems.length === 0 ? (
                            <TableRow><TableCell colSpan={9} className="py-14 text-center"><div className="flex flex-col items-center gap-2 text-slate-400"><Inbox className="w-8 h-8" /><div className="text-sm">Belum ada reject perbaikan</div></div></TableCell></TableRow>
                        ) : pageItems.map((row) => {
                            const nilai = Number(row.jumlah_pcs || 0) * Number(row.harga_per_pcs_snapshot || 0);
                            const days = row.days_remaining ?? 0;
                            return (
                                <TableRow key={row.id} data-testid={`reject-row-${row.id}`} className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors">
                                    <TableCell className="font-semibold text-teal-700">{row.nomor_rp}</TableCell>
                                    <TableCell className="text-sm text-slate-700">{vendorMap[row.vendor_id] || "-"}</TableCell>
                                    <TableCell className="text-sm text-slate-800">{row.nama_produk_snapshot}</TableCell>
                                    <TableCell className="text-sm text-slate-800 text-right num-tabular">{row.jumlah_pcs} pcs</TableCell>
                                    <TableCell className="text-sm font-semibold text-slate-900 text-right num-tabular">{formatRupiah(nilai)}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{formatTanggal(row.tanggal_masuk)}</TableCell>
                                    <TableCell>
                                        <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${deadlineTone(days, row.status)}`}>
                                            {formatTanggal(row.deadline)}
                                            {row.status === "menunggu" && <span className="ml-1 num-tabular">({days >= 0 ? `${days}h` : `${Math.abs(days)}h lewat`})</span>}
                                        </span>
                                    </TableCell>
                                    <TableCell><span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${STATUS_TONE[row.status]}`}>{STATUS_LABEL[row.status]}</span></TableCell>
                                    <TableCell className="text-right">
                                        {row.status === "menunggu" && canMark && (
                                            <button onClick={() => setConfirmSelesai(row)} data-testid={`reject-mark-selesai-${row.id}`} className="text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1 transition-colors">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Selesai
                                            </button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {!loading && items.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                        <div className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, items.length)} dari {items.length}</div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg h-8">Sebelumnya</Button>
                            <span className="text-xs text-slate-600 px-2">Hal. {page}/{totalPages}</span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="rounded-lg h-8">Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            <AlertDialog open={!!confirmSelesai} onOpenChange={(o) => !o && setConfirmSelesai(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Tandai Selesai Diperbaiki</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold">{confirmSelesai?.nomor_rp}</span> — {confirmSelesai?.nama_produk_snapshot} ({confirmSelesai?.jumlah_pcs} pcs) akan ditandai selesai diperbaiki dan nilainya masuk sebagai kredit di invoice berikutnya. Lanjutkan?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={markSelesai} disabled={processing} data-testid="reject-confirm-selesai-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                            {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...</> : "Ya, Selesai"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
